import React, { useCallback, useMemo, useState } from 'react'
import { Grid, Stack, Typography } from '@mui/material'
import { translate } from '../i18n'
import LoadingImage from '../components/LoadingImage'
import { ProjectMobileBannerProps } from './ProjectMobileBanner.types'
import { AnyComponentRef, AnyInput, AnySwitch, AnyUploader } from '../anyform'
import { Alert, useS3Client } from '../components'

function ProjectMobileBanner({
    banner,
    registerInner,
    clearInner,
}: ProjectMobileBannerProps) {
    const s3Client = useS3Client()
    const [path, setPath] = useState(banner?.image)
    const [required, setRequired] = useState(!!banner)
    const isProduction = useMemo(() => {
        return process.env.REACT_APP_ENV === 'production'
    }, [])

    const hasChangedImage = useCallback((name: string, value?: string) => {
        setPath(value)
    }, [])

    const registerBanner = useCallback(
        (name: string, ref: AnyComponentRef<any>) => {
            registerInner('banner', name, ref)
        },
        [registerInner]
    )

    const onChangedEnabled = useCallback(
        (name: string, value?: boolean) => {
            setRequired(!!value)
            if (!value) {
                clearInner()
            }
        },
        [clearInner]
    )

    return (
        <Grid
            container
            justifyContent="flex-start"
            justifyItems="center"
            alignContent="center"
            alignItems="flex-start"
            spacing={2}
            sx={{ py: 2 }}
        >
            <Grid item xs={12}>
                <AnySwitch
                    label={translate('projects.banner.section')}
                    register={registerBanner}
                    name="enabled"
                    initialValue={required}
                    notifier={onChangedEnabled}
                />
                <Typography variant="body2">
                    {translate('projects.banner.text')}
                </Typography>
            </Grid>
            <Grid item xs={12} sx={required ? undefined : { display: 'none' }}>
                <Grid container spacing={2}>
                    <Grid item xs={12}>
                        <AnyInput
                            type="text"
                            name="title"
                            label={translate('projects.banner.title') as string}
                            initialValue={banner?.title}
                            required={required}
                            maxLength={30}
                            register={registerBanner}
                        />
                        <AnyInput
                            type="text"
                            name="subtitle"
                            label={
                                translate('projects.banner.subtitle') as string
                            }
                            initialValue={banner?.subtitle}
                            required={required}
                            maxLength={56}
                            register={registerBanner}
                        />
                    </Grid>
                    <Grid item xs={12} md={5}>
                        <LoadingImage path={path} />
                    </Grid>
                    <Grid item xs={12} md={7}>
                        <AnyUploader
                            name="image"
                            label={translate('projects.banner.image') as string}
                            s3Client={s3Client}
                            initialValue={banner?.image}
                            acceptExtension={'.jpg,.png'}
                            path={'banners'}
                            required={required}
                            imageDimensions={{ width: 1800, height: 1200 }}
                            register={registerBanner}
                            notifier={hasChangedImage}
                        />
                        {!isProduction && (
                            <Stack mt={1}>
                                <Alert
                                    variant="info"
                                    title={translate(
                                        'projects.banner.not_enabled'
                                    )}
                                    plain
                                    elevation={0}
                                />
                            </Stack>
                        )}
                    </Grid>
                </Grid>
            </Grid>
        </Grid>
    )
}

export default ProjectMobileBanner
